<template>
  <footer class="pagination-bar" aria-label="Payment pagination">
    <div class="pagination-controls">
      <button type="button" class="pagination-btn prev" aria-label="Previous page" :disabled="loading || page <= 1" @click="change(page - 1)"><AppointmentIcon name="chevron-left" /></button>
      <template v-for="(value, index) in pages" :key="index">
        <span v-if="value === '…'" class="pagination-ellipsis">…</span>
        <button v-else type="button" class="pagination-btn page-number" :class="{ active: page === value }" :aria-label="`Page ${value}`"
          :aria-current="page === value ? 'page' : undefined" :disabled="loading" @click="change(value)">{{ value }}</button>
      </template>
      <button type="button" class="pagination-btn next" aria-label="Next page" :disabled="loading || page >= pageCount" @click="change(page + 1)"><AppointmentIcon name="chevron-right" /></button>
    </div>
    <div class="pagination-results">Results: {{ start }} - {{ end }} of {{ total }}</div>
  </footer>
</template>
<script setup>
import { computed } from 'vue';
import AppointmentIcon from '@/components/icons/AppointmentIcon.vue';
const props = defineProps({ page: { type: Number, default: 1 }, pageCount: { type: Number, default: 1 }, pageSize: { type: Number, default: 25 }, total: { type: Number, default: 0 }, loading: Boolean });
const emit = defineEmits(['change']);
const start = computed(() => props.total ? (props.page - 1) * props.pageSize + 1 : 0);
const end = computed(() => Math.min(props.page * props.pageSize, props.total));
const pages = computed(() => {
  const count = Math.max(1, props.pageCount);
  if (count <= 7) return Array.from({ length: count }, (_, i) => i + 1);
  const start = Math.max(2, Math.min(props.page - 1, count - 4));
  const end = Math.min(count - 1, Math.max(props.page + 1, 5));
  return [1, ...(start > 2 ? ['…'] : []), ...Array.from({ length: end - start + 1 }, (_, i) => start + i), ...(end < count - 1 ? ['…'] : []), count];
});
const change = page => { if (!props.loading && page !== props.page && page >= 1 && page <= props.pageCount) emit('change', page); };
</script>
<style scoped>
.pagination-bar {
	display: flex;
	align-items: center;
	justify-content: flex-start;
	padding: 4px 16px;
	margin-top: 12px;
	border-radius: 8px;
	background: transparent;
	border: none;
	box-shadow: none;
	flex-wrap: wrap;
	gap: 16px;
}

.pagination-results {
	font: var(--ap-type-caption);
	font-variant-numeric: tabular-nums;
	color: var(--text-color-secondary);
	white-space: nowrap;
}

.pagination-controls {
	display: flex;
	flex-wrap: wrap;
	align-items: center;
	gap: 8px;
}

.pagination-btn {
	display: inline-flex;
	align-items: center;
	justify-content: center;
	min-width: 34px;
	height: 34px;
	padding: 0 8px;
	border: 1px solid var(--border-color);
	background: var(--noc-background);
	border-radius: 8px;
	color: var(--text-color);
	font: var(--ap-type-control);
	font-variant-numeric: tabular-nums;
	cursor: pointer;
	box-shadow: none;
	transition: background-color 0.15s ease, border-color 0.15s ease, color 0.15s ease;
}

.pagination-btn:hover:not(:disabled):not(.active) {
	background-color: color-mix(in srgb, var(--text-color-secondary) 4%, transparent);
}

.pagination-btn.active {
	background-color: var(--noc-blue-05);
	border-color: var(--noc-blue-15);
	color: var(--noc-blue);
	cursor: default;
}

.pagination-btn:disabled {
	opacity: 0.4;
	cursor: not-allowed;
}

.pagination-btn:focus-visible { outline: 2px solid var(--noc-blue); outline-offset: 2px; }
.pagination-ellipsis { color: var(--text-color-secondary); padding: 0 4px; }
@media (any-pointer: coarse) { .pagination-btn { touch-action: manipulation; } }
</style>
